/**
 * Per-faction signature skill VFX (boss 2026-06-16「太多一樣了 你找不到其他動畫嗎」).
 *
 * 五招之前全用同一張爆炸序列圖(ring/pixel-explosion)只換 tint → 看起來都一樣。
 * 這裡改用 Kenney CC0 粒子貼圖(public/assets/vfx/particles/*.png),讓每個門派有
 * 「本質不同」的動畫:
 *   星算 celestial → 星辰灑落(star_*)
 *   符文 runic     → 旋轉魔法陣(magic_* / symbol_* / circle_* / twirl_*)
 *   機械 techno    → 雷射齊射(trace_* / muzzle_* / spark_*)
 *   自然 elemental → 真火焰粒子柱(flame_* / fire_*)
 *   疆域 dominion  → 隕石 + 爆炸(維持 explosion-vfx,成為唯一的「爆炸系」= 自然區隔)
 *
 * 全程 try-wrapped + texture-existence-gated:fresh checkout / stripped build /
 * headless 測試都安靜 no-op,絕不丟錯、絕不影響技能機制(傷害另由 scene 結算)。
 */
import Phaser from 'phaser';

/** Kenney CC0 particle textures copied to a clean (space/paren-free) path. */
export const SKILL_PARTICLE_FILES: readonly string[] = [
  'star_01', 'star_04', 'star_06', 'star_08',
  'magic_01', 'magic_03', 'magic_05', 'symbol_01', 'symbol_02', 'twirl_01', 'twirl_02', 'circle_03', 'circle_05',
  'trace_02', 'trace_03', 'muzzle_03', 'muzzle_04', 'spark_03', 'spark_05', 'spark_07',
  'flame_02', 'flame_04', 'flame_06', 'fire_01', 'fire_02',
  'light_02', 'light_03', 'flare_01', 'smoke_05', 'scorch_02',
];

/** Queue every particle texture on the scene loader. Call from preload(). */
export function preloadSkillParticles(scene: Phaser.Scene): void {
  for (const f of SKILL_PARTICLE_FILES) {
    try {
      if (!scene.textures?.exists?.(f)) scene.load.image(f, `assets/vfx/particles/${f}.png`);
    } catch { /* headless harness lacks load.image */ }
  }
}

/** Side-effect callbacks owned by the scene (juice / sfx / camera). */
export interface SkillFxHooks {
  shake?: (intensity: number, ms: number) => void;
  hitStop?: (ms: number) => void;
  sfx?: (kind: string, gain: number) => void;
  flash?: (ms: number, r: number, g: number, b: number) => void;
}

export interface TargetPoint { x: number; y: number; }

/** Render depth — sit above the cut-in 立繪 container (depth 880). */
const DZ = 890;

/** Add an additive, depth-set image. Returns null if texture absent / headless. */
function img(
  scene: Phaser.Scene, key: string, x: number, y: number, scale: number, tint?: number, depth = DZ + 1,
): Phaser.GameObjects.Image | null {
  try {
    if (!scene.textures?.exists?.(key)) return null;
    const o = scene.add.image(x, y, key).setDepth(depth).setScale(scale);
    if (typeof tint === 'number') o.setTint(tint);
    try { o.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
    return o;
  } catch { return null; }
}

/** 敵人座標;若場上沒敵人(波次間)就在可見戰場右半隨機灑幾點,讓招式仍看得到。 */
function targetsOrDefault(scene: Phaser.Scene, targets: TargetPoint[], n: number): TargetPoint[] {
  if (targets.length > 0) return targets;
  const W = scene.scale?.width || 800; const H = scene.scale?.height || 600;
  const pts: TargetPoint[] = [];
  for (let i = 0; i < n; i++) {
    pts.push({ x: W * (0.45 + 0.45 * Math.random()), y: H * (0.25 + 0.5 * Math.random()) });
  }
  return pts;
}

/** 鋸齒閃電:(x1,y1)→(x2,y2) 折線,粗外光暈 + 白色亮核,快速淡出。boss 2026-06-16
 *  「綠色(機械)大招閃電太不明顯」→ 用這個取代細細的 trace beam,清楚看得到閃電打出去。 */
function lightningBolt(scene: Phaser.Scene, x1: number, y1: number, x2: number, y2: number, color: number, depth: number): void {
  try {
    const g = scene.add.graphics().setDepth(depth);
    try { g.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
    const segs = 6;
    const dx = (x2 - x1) / segs; const dy = (y2 - y1) / segs;
    let nx = -(y2 - y1); let ny = (x2 - x1);
    const nlen = Math.hypot(nx, ny) || 1; nx /= nlen; ny /= nlen;
    const pts: { x: number; y: number }[] = [{ x: x1, y: y1 }];
    for (let i = 1; i < segs; i++) {
      const off = (Math.random() - 0.5) * 30;
      pts.push({ x: x1 + dx * i + nx * off, y: y1 + dy * i + ny * off });
    }
    pts.push({ x: x2, y: y2 });
    const stroke = (w: number, c: number, a: number) => {
      g.lineStyle(w, c, a); g.beginPath(); g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
      g.strokePath();
    };
    // boss 2026-06-17「閃電要細的」:三層 stroke 變細(8/4/2 → 4/2/1)。
    stroke(4, color, 0.3);    // 外光暈(細)
    stroke(2, color, 0.85);   // 中層
    stroke(1, 0xffffff, 0.95); // 白色亮核
    scene.tweens.add({ targets: g, alpha: 0, duration: 260, ease: 'Quad.easeIn', onComplete: () => { try { g.destroy(); } catch { /* noop */ } } });
  } catch { /* headless */ }
}

type EmitterConfig = Phaser.Types.GameObjects.Particles.ParticleEmitterConfig;

function emit(scene: Phaser.Scene, key: string, x: number, y: number, cfg: EmitterConfig, depth: number): void {
  try {
    if (!scene.textures?.exists?.(key)) return;
    const em = scene.add.particles(x, y, key, cfg);
    em.setDepth(depth);
    scene.time.delayedCall(800, () => { try { em.stop(); } catch { /* noop */ } });
    scene.time.delayedCall(1800, () => { try { em.destroy(); } catch { /* noop */ } });
  } catch { /* headless / no particles factory */ }
}

// ── 星算門:星辰灑落 ──────────────────────────────────────────────────────────
export function playCelestialStars(
  scene: Phaser.Scene, px: number, _py: number, targets: TargetPoint[], hooks: SkillFxHooks,
): void {
  try {
    const W = scene.scale?.width || 800;
    // 滿天落星(從上方灑落,藍金白閃)
    emit(scene, 'star_06', 0, 0, {
      x: { min: px, max: W }, y: -20, lifespan: 1000,
      speedY: { min: 280, max: 460 }, speedX: { min: -50, max: 50 },
      scale: { start: 0.16, end: 0.03 }, alpha: { start: 1, end: 0.15 },
      tint: [0xffffff, 0x8fd0ff, 0xffe9a8], blendMode: 'ADD', frequency: 28, quantity: 2,
    } as EmitterConfig, DZ);
    // 逐敵落大星 + flare 命中閃
    targets.slice(0, 12).forEach((t, i) => {
      scene.time.delayedCall(80 + i * 70, () => {
        const s = img(scene, 'star_01', t.x, t.y - 280, 0.34, 0x9fd6ff);
        if (!s) return;
        scene.tweens.add({
          targets: s, y: t.y, scaleX: 0.12, scaleY: 0.12, duration: 250, ease: 'Quad.easeIn',
          onComplete: () => {
            try { s.destroy(); } catch { /* noop */ }
            const f = img(scene, 'flare_01', t.x, t.y, 0.06, 0xbfe4ff);
            if (f) scene.tweens.add({ targets: f, scaleX: 0.55, scaleY: 0.55, alpha: 0, duration: 320, onComplete: () => { try { f.destroy(); } catch { /* noop */ } } });
          },
        });
      });
    });
    hooks.shake?.(0.011, 130);
    hooks.flash?.(140, 150, 200, 255);
    hooks.sfx?.('xp-popup', 0.6);
  } catch { /* headless */ }
}

// ── 符文門:旋轉魔法陣(符文審判,降臨在敵人身上)──────────────────────────────
// boss 2026-06-16「紫苑我看不出來效果」:原本魔法陣畫在玩家身上(x≈80)被 cut-in 立繪
// 蓋住 → 看不到。改成每個敵人頭上「降臨魔法陣 → 收束 → 符文字符 + 漩渦命中」,跟其他
// 攻擊招一樣打在敵人身上(在可見的右半場),才看得見。
export function playRunicRunes(
  scene: Phaser.Scene, _px: number, _py: number, targets: TargetPoint[], hooks: SkillFxHooks,
): void {
  try {
    const COL = 0x9b6bff;
    const glyphTex = ['magic_01', 'magic_03', 'magic_05', 'symbol_01', 'symbol_02'];
    const pts = targetsOrDefault(scene, targets, 5);
    pts.slice(0, 14).forEach((t, i) => {
      scene.time.delayedCall(i * 55, () => {
        // 魔法陣降臨:大圈 → 旋轉收束到敵人身上(審判鎖定)
        const ring = img(scene, 'circle_05', t.x, t.y, 0.55, COL, DZ);
        if (ring) {
          ring.setAlpha(0.9);
          scene.tweens.add({ targets: ring, scaleX: 0.18, scaleY: 0.18, angle: 170, duration: 280, ease: 'Cubic.easeIn' });
          scene.tweens.add({ targets: ring, alpha: 0, delay: 240, duration: 200, onComplete: () => { try { ring.destroy(); } catch { /* noop */ } } });
        }
        // 符文字符閃現
        const g = img(scene, glyphTex[i % glyphTex.length], t.x, t.y, 0.22, COL, DZ + 1);
        if (g) scene.tweens.add({ targets: g, scaleX: 0.07, scaleY: 0.07, angle: 90, alpha: 0, delay: 200, duration: 260, onComplete: () => { try { g.destroy(); } catch { /* noop */ } } });
        // 漩渦命中爆
        scene.time.delayedCall(240, () => {
          const tw = img(scene, 'twirl_02', t.x, t.y, 0.06, COL, DZ + 1);
          if (tw) scene.tweens.add({ targets: tw, scaleX: 0.44, scaleY: 0.44, angle: 220, alpha: 0, duration: 320, onComplete: () => { try { tw.destroy(); } catch { /* noop */ } } });
        });
      });
    });
    hooks.shake?.(0.012, 160);
    hooks.flash?.(130, 155, 107, 255);
    hooks.sfx?.('skill-shield', 0.9);
  } catch { /* headless */ }
}

// ── 機械門:雷射齊射 ──────────────────────────────────────────────────────────
export function playTechnoBarrage(
  scene: Phaser.Scene, px: number, py: number, targets: TargetPoint[], hooks: SkillFxHooks,
): void {
  try {
    const COL = 0x35e0c8;
    // 砲口 muzzle 閃
    const mz = img(scene, 'muzzle_03', px + 22, py, 0.26, COL);
    if (mz) scene.tweens.add({ targets: mz, scaleX: 0.42, scaleY: 0.42, alpha: 0, duration: 220, onComplete: () => { try { mz.destroy(); } catch { /* noop */ } } });
    const pts = targetsOrDefault(scene, targets, 5);
    pts.slice(0, 14).forEach((t, i) => {
      scene.time.delayedCall(50 + i * 55, () => {
        // 鋸齒閃電射向敵人。boss 2026-06-17「閃電要斜的、細的、可多條」→ 主道 + 2-3 條
        // 斜分叉:終點 Y 上下大幅偏移(斜),各自細鋸齒,看起來多條斜閃電。
        lightningBolt(scene, px, py, t.x, t.y, COL, DZ + 1);
        const forks = 2 + (Math.random() < 0.5 ? 1 : 0);
        for (let k = 0; k < forks; k++) {
          const sign = k % 2 === 0 ? -1 : 1;
          const ey = t.y + sign * (24 + Math.random() * 44);   // 斜:終點 Y 大幅偏移
          const ex = t.x + (Math.random() - 0.5) * 50;
          lightningBolt(scene, px, py, ex, ey, COL, DZ + 1);
        }
        // 命中:大火花 + 白色閃光點
        const sp = img(scene, 'spark_05', t.x, t.y, 0.42, COL);
        if (sp) scene.tweens.add({ targets: sp, scaleX: 0.7, scaleY: 0.7, alpha: 0, angle: 60, duration: 320, onComplete: () => { try { sp.destroy(); } catch { /* noop */ } } });
        const fl = img(scene, 'flare_01', t.x, t.y, 0.05, 0xffffff);
        if (fl) scene.tweens.add({ targets: fl, scaleX: 0.32, scaleY: 0.32, alpha: 0, duration: 240, onComplete: () => { try { fl.destroy(); } catch { /* noop */ } } });
      });
    });
    hooks.shake?.(0.012, 150);
    hooks.flash?.(120, 90, 230, 220);
    hooks.sfx?.('skill-scan', 0.9);
  } catch { /* headless */ }
}

// ── 自然門:真火焰粒子柱 ──────────────────────────────────────────────────────
export function playElementalFlames(
  scene: Phaser.Scene, cx: number, cy: number, h: number, hooks: SkillFxHooks,
): void {
  try {
    const GREEN = [0x2e9e3a, 0x43c94d, 0x76dd5a];
    // 火焰粒子往上竄(真火焰貼圖,非橢圓非爆炸)
    emit(scene, 'flame_04', cx, cy + h * 0.42, {
      lifespan: 680, speedY: { min: -270, max: -120 }, speedX: { min: -45, max: 45 },
      scale: { start: 0.22, end: 0.02 }, alpha: { start: 0.95, end: 0 },
      tint: GREEN, blendMode: 'ADD', frequency: 20, quantity: 3,
    } as EmitterConfig, DZ + 1);
    // 底部點燃大火球
    const fb = img(scene, 'fire_02', cx, cy + h * 0.2, 0.1, 0x55dd44);
    if (fb) scene.tweens.add({ targets: fb, scaleX: 0.55, scaleY: 0.65, alpha: 0, duration: 440, onComplete: () => { try { fb.destroy(); } catch { /* noop */ } } });
    hooks.shake?.(0.014, 200);
    hooks.hitStop?.(40);
    hooks.sfx?.('skill-flame', 0.95);
    hooks.flash?.(150, 110, 255, 120);
  } catch { /* headless */ }
}
